import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, withModifiers as _withModifiers, withKeys as _withKeys, createVNode as _createVNode, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, withCtx as _withCtx, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createElementBlock as _createElementBlock, renderSlot as _renderSlot, createTextVNode as _createTextVNode, normalizeClass as _normalizeClass, renderList as _renderList, Fragment as _Fragment, normalizeStyle as _normalizeStyle } from "vue"

const _hoisted_1 = {
  key: 0,
  class: "filterable"
}
const _hoisted_2 = { class: "autocomplete" }
const _hoisted_3 = { class: "value" }
const _hoisted_4 = { class: "ghost" }
const _hoisted_5 = { class: "toggle-button-content" }
const _hoisted_6 = {
  key: 0,
  class: "select-toggle-text"
}
const _hoisted_7 = {
  key: 1,
  class: "select-toggle-placeholder-text"
}
const _hoisted_8 = { class: "select-toggle-caret" }
const _hoisted_9 = ["id"]
const _hoisted_10 = { class: "p-2 mb-0" }
const _hoisted_11 = ["data-cy", "onClick"]

import Icon from "@common/components/Icon.vue";
import { Guid } from "@util/guid";
import { useScroll } from "@vueuse/core";
import _ from "lodash";
import { Ref, WatchStopHandle, computed, nextTick, reactive, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import MButton from "./MButton.vue";
import MInput from "./MInput.vue";
import EmptyState from "@common/components/EmptyState.vue";

//#region Refs
type ViewModel = { isOpen: boolean; menuPositionX: number; menuPositionY: number; selectToggleWidth: number; validationFeedback: string };

export default /*#__PURE__*/_defineComponent({
  __name: 'MSelect',
  props: {
    value: {},
    items: {},
    placeholder: {},
    width: {},
    textField: { default: "name" },
    valueField: {},
    translateTextField: { type: Boolean },
    filterable: { type: Boolean }
  },
  emits: ["input", "selection", "cleared"],
  setup(__props: any, { expose: __expose, emit: emits }) {

const props = __props;

const selfRef = ref<HTMLElement | null>(null);
const selectMenu = ref<HTMLElement | null>(null);
const selectToggle = ref<typeof MButton | typeof MInput | null>(null);
let scrollParent: Ref<HTMLElement> | null;

const selectedValue = ref<string | null>(null);
const activeIndex = ref(0);
//#endregion

//#region General
const id = Guid.new();

const { t: $t } = useI18n();



//#endregion

//#region Viewmodel
const vm = reactive<ViewModel>({ isOpen: false, menuPositionX: 0, menuPositionY: 0, selectToggleWidth: 0, validationFeedback: "" });
//#endregion

//#region Helpers
const getEntryKey = (entry: any): string | number | symbol | undefined => {
  if (typeof entry == "string") return entry;
  const keyValue = props.valueField ? entry[props.valueField] : entry[props.textField];
  if (typeof keyValue === "string" || typeof keyValue === "number" || typeof keyValue === "symbol" || typeof keyValue === "undefined") return keyValue;
};
const getEntryText = (entry: any, getTranslated: boolean = false) => {
  if (typeof entry == "string") return entry;
  const textFieldValue: any = entry?.[props.textField];
  if (!entry || !textFieldValue) return "";
  const result = getTranslated ? $t(textFieldValue) : textFieldValue;
  return result;
};
//#endregion

//#region SelectedEntry
const consolidatedItems = computed(() => (_.isArray(props.items) ? props.items : flatObject(props.items)));

const selectedEntry = computed({
  get: () => {
    return consolidatedItems.value.find((x) => (props.valueField ? x[props.valueField] == props.value : x == props.value)) ?? null;
  },
  set: (nv: any) => {
    emits("input", props.valueField ? nv[props.valueField] : nv);
  }
});

const entryClickedHandler = (entry: any) => {
  if (entry?.disabled) return;
  selectedEntry.value = entry;

  emits("selection", entry);
  hide();
};
//#endregion

//#region ScrollHandle
let isScrollingWatcher: WatchStopHandle | null = null;

const enableCloseOnScroll = () => {
  const foundParent = getScrollParent(selfRef.value);
  scrollParent = foundParent ? ref(foundParent) : null;
  if (scrollParent?.value) {
    const { isScrolling } = useScroll(scrollParent);
    isScrollingWatcher = watch(isScrolling, () => {
      hide();
    });
  }
};

function getScrollParent(node: HTMLElement | ParentNode | null) {
  if (node == null) return null;
  if (node instanceof HTMLElement && node.scrollHeight > node.clientHeight) {
    const computedStyle = getComputedStyle(node);
    if (
      computedStyle.overflow == "auto" ||
      computedStyle.overflowY == "auto" ||
      computedStyle.overflowX == "auto" ||
      computedStyle.overflow == "scroll" ||
      computedStyle.overflowY == "scroll" ||
      computedStyle.overflowX == "scroll"
    )
      return node;
    else return getScrollParent(node.parentNode);
  } else {
    return getScrollParent(node.parentNode);
  }
}
//#endregion

//#region Filterable
const filteredItems = computed(() => {
  if (!Array.isArray(props.items)) return [];
  if (!selectedValue.value) return props.items;
  return props.items.filter((item) => getEntryText(item).toLowerCase().startsWith(selectedValue.value!.toLowerCase()));
});

const suggestedEntry = computed(() => {
  if (selectedValue.value === "") return null;
  const item = filteredItems.value[activeIndex.value] || null;
  return item;
});

const ghostValue = computed(() => {
  if (!selectedValue.value || !suggestedEntry.value) return "";
  const entryText = getEntryText(suggestedEntry.value);
  const prefixLen = commonPrefixLength(selectedValue.value, entryText);
  return entryText.slice(prefixLen);
});

function commonPrefixLength(a: string, b: string) {
  const len = Math.min(a.length, b.length);
  for (let i = 0; i < len; i++) {
    if (a[i].toLowerCase() !== b[i].toLowerCase()) {
      return i;
    }
  }
  return len;
}

const clearFilterValue = () => {
  selectedValue.value = "";
  emits("cleared");
  const inputEl = selectToggle.value?.$el instanceof HTMLInputElement ? selectToggle.value.$el : null;
  inputEl?.focus();
};

const onArrowUp = () => {
  if (!vm.isOpen) show();
  if (activeIndex.value == 0) {
    activeIndex.value = filteredItems.value.length - 1;
  } else {
    activeIndex.value--;
  }
  nextTick(() => {
    selectMenu.value?.querySelector(".select-menu-entry.suggest")?.scrollIntoView(false);
  });
};

const onArrowDown = () => {
  if (!vm.isOpen) show();
  if (activeIndex.value === filteredItems.value.length - 1) {
    activeIndex.value = 0;
  } else {
    activeIndex.value++;
  }
  nextTick(() => {
    selectMenu.value?.querySelector(".select-menu-entry.suggest")?.scrollIntoView(true);
  });
};

const onClick = () => {
  if (filteredItems.value.length && getEntryText(selectedEntry.value) !== selectedValue.value && !vm.isOpen) show();
};

const onEnter = () => {
  if (suggestedEntry.value) {
    selectedValue.value = getEntryText(suggestedEntry.value);
    activeIndex.value = 0;
    entryClickedHandler(suggestedEntry.value);
  }
};

const onTab = () => {
  onEnter();
};

watch(
  () => selectedValue.value,
  (nv) => {
    if (!nv) {
      clearFilterValue();
    }
  }
);

watch(
  () => selectedEntry.value,
  () => {
    selectedValue.value = selectedEntry.value ? getEntryText(selectedEntry.value) : "";
    nextTick(() => {
      hide();
    });
  },
  { immediate: true }
);

watch(
  () => suggestedEntry.value,
  (nv) => {
    if (!props.filterable) return;
    if (selectedValue.value && !nv) {
      vm.validationFeedback = $t("noMatch", { value: selectedValue.value });
    } else {
      vm.validationFeedback = "";
    }
    if (nv || vm.validationFeedback) {
      show();
    }
  },
  { deep: true }
);

watch(
  () => filteredItems.value.length,
  (nv, ov) => {
    if (nv !== ov) {
      calculatePositionOfMenu();
    }
  }
);

function flatObject(object: Record<string, any[]>) {
  const result = [];
  for (const groupName in object) {
    const group = object[groupName];

    if (Array.isArray(group)) {
      for (const item of group) {
        result.push({
          ...item,
          groupName
        });
      }
    }
  }
  return result;
}

function checkGroupName(entryIndex: number, entry: any) {
  if (entryIndex === 0) return true;
  const previousEntry = consolidatedItems.value[entryIndex - 1];
  return previousEntry.groupName !== entry.groupName;
}
//#endregion

const show = () => {
  if (selectMenu.value) {
    selectMenu.value.showPopover();
  }
};

const hide = () => {
  if (selectMenu.value) {
    activeIndex.value = 0;
    selectMenu.value.hidePopover();
  }
};
const toggle = () => {
  if (selectMenu.value) {
    selectMenu.value.togglePopover(!vm.isOpen);
  }
};

const setState = () => {
  if (selectMenu.value) {
    vm.isOpen = selectMenu.value.matches(":popover-open");
    if (vm.isOpen) {
      calculatePositionOfMenu();
      enableCloseOnScroll();
    } else if (isScrollingWatcher) {
      isScrollingWatcher();
      isScrollingWatcher = null;
    }
  }
};

const calculatePositionOfMenu = () => {
  vm.menuPositionX = 0;
  vm.menuPositionY = 0;
  const selectToggleRect =
    selectToggle.value instanceof HTMLInputElement ? selectToggle.value.getBoundingClientRect() : selectToggle.value?.$el?.getBoundingClientRect();
  vm.selectToggleWidth = selectToggleRect ? selectToggleRect.width : 0;

  nextTick(() => {
    if (selectMenu.value && selectToggle.value) {
      const menuRect = selectMenu.value.getBoundingClientRect();
      let x = 0;
      let y = 0;

      if (selectToggleRect && menuRect) {
        x = selectToggleRect.x - menuRect.x;
        y = selectToggleRect.y - menuRect.y + selectToggleRect.height;
        if (menuRect.bottom + y > window.innerHeight) {
          y = selectToggleRect.y - menuRect.bottom;
        }
      }
      vm.menuPositionX = x;
      vm.menuPositionY = y;
    }
  });
};

__expose({ show, hide, toggle });

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", {
    class: "m-select",
    style: _normalizeStyle({ width: _ctx.width ?? '' }),
    ref_key: "selfRef",
    ref: selfRef
  }, [
    (_ctx.filterable)
      ? (_openBlock(), _createElementBlock("div", _hoisted_1, [
          _createVNode(MInput, {
            placeholder: props.placeholder ?? `<${_unref($t)('none')}>`,
            type: "text",
            class: "m-select-toggle",
            modelValue: selectedValue.value,
            "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((selectedValue).value = $event)),
            autocomplete: "off",
            ref_key: "selectToggle",
            ref: selectToggle,
            popovertarget: `select-${_unref(id)}`,
            onKeydown: [
              _withKeys(_withModifiers(onArrowDown, ["prevent"]), ["down"]),
              _withKeys(_withModifiers(onArrowUp, ["prevent"]), ["up"]),
              _withKeys(_withModifiers(onEnter, ["prevent"]), ["enter"]),
              _withKeys(_withModifiers(onTab, ["prevent"]), ["tab"])
            ],
            onClick: onClick
          }, null, 8, ["placeholder", "modelValue", "popovertarget", "onKeydown"]),
          _createElementVNode("div", _hoisted_2, [
            _createElementVNode("div", _hoisted_3, _toDisplayString(selectedValue.value), 1),
            _createElementVNode("div", _hoisted_4, _toDisplayString(ghostValue.value), 1)
          ]),
          (selectedValue.value)
            ? (_openBlock(), _createBlock(MButton, {
                key: 0,
                "design-style": "mono",
                class: "toggle",
                "only-icon": "",
                onClick: clearFilterValue
              }, {
                default: _withCtx(() => [
                  _createVNode(Icon, { icon: "Close" })
                ]),
                _: 1
              }))
            : (_openBlock(), _createBlock(MButton, {
                key: 1,
                "design-style": "mono",
                class: "toggle",
                "only-icon": "",
                onClick: show
              }, {
                default: _withCtx(() => [
                  _createVNode(Icon, { icon: "ChevronBottom" })
                ]),
                _: 1
              }))
        ]))
      : (_openBlock(), _createBlock(MButton, {
          key: 1,
          "design-style": "select",
          class: _normalizeClass(["m-select-toggle", { active: vm.isOpen, error: !selectedEntry.value && !_ctx.placeholder }]),
          ref_key: "selectToggle",
          ref: selectToggle,
          popovertarget: `select-${_unref(id)}`,
          "data-cy": "select-condition-button"
        }, {
          default: _withCtx(() => [
            _createElementVNode("div", _hoisted_5, [
              (selectedEntry.value)
                ? (_openBlock(), _createElementBlock("span", _hoisted_6, [
                    _renderSlot(_ctx.$slots, "button-content", {
                      selectedItem: selectedEntry.value,
                      selectedText: getEntryText(selectedEntry.value, _ctx.translateTextField)
                    }, () => [
                      _createTextVNode(_toDisplayString(getEntryText(selectedEntry.value, _ctx.translateTextField)), 1)
                    ])
                  ]))
                : (_openBlock(), _createElementBlock("div", _hoisted_7, _toDisplayString(_ctx.placeholder ?? `<${_unref($t)("none")}>`), 1)),
              _createElementVNode("div", _hoisted_8, [
                _createVNode(Icon, { icon: "ChevronBottom" })
              ])
            ])
          ]),
          _: 3
        }, 8, ["class", "popovertarget"])),
    _createElementVNode("div", {
      id: `select-${_unref(id)}`,
      class: _normalizeClass(["m-select-menu", { 'is-open': vm.isOpen }]),
      popover: "",
      ref_key: "selectMenu",
      ref: selectMenu,
      onToggle: setState,
      style: _normalizeStyle({
        transform: `translate(${vm.menuPositionX}px, ${vm.menuPositionY}px)`,
        minWidth: `${vm.selectToggleWidth}px`
      })
    }, [
      (!vm.validationFeedback)
        ? _renderSlot(_ctx.$slots, "default", {
            key: 0,
            items: _ctx.filterable ? filteredItems.value : consolidatedItems.value
          }, () => [
            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_ctx.filterable ? filteredItems.value : consolidatedItems.value, (entry, index) => {
              return (_openBlock(), _createElementBlock(_Fragment, {
                key: getEntryKey(entry)
              }, [
                ('groupName' in entry && entry.groupName && checkGroupName(index, entry))
                  ? _renderSlot(_ctx.$slots, "item-template", {
                      key: 0,
                      entry: entry,
                      groupName: _ctx.groupName
                    }, () => [
                      _createElementVNode("h6", _hoisted_10, _toDisplayString(entry.groupName), 1)
                    ])
                  : _createCommentVNode("", true),
                _createElementVNode("div", {
                  "data-cy": 'select-menu-entry-' + getEntryText(entry),
                  class: _normalizeClass(["select-menu-entry", {
              active: _ctx.valueField ? entry[_ctx.valueField] == selectedEntry.value?.[_ctx.valueField] : entry == selectedEntry.value,
              suggest: entry === suggestedEntry.value,
              disabled: !_unref(_).isString(entry) ? entry?.disabled : undefined,
              'separator-before': !_unref(_).isString(entry) ? entry?.separator === 'top' : undefined,
              'separator-after': !_unref(_).isString(entry) ? entry?.separator === 'bottom' : undefined,
              'pl-3': entry.groupName
            }]),
                  onClick: ($event: any) => (entryClickedHandler(entry))
                }, [
                  _renderSlot(_ctx.$slots, "item-template", { entry: entry }, () => [
                    _createTextVNode(_toDisplayString(getEntryText(entry, _ctx.translateTextField)), 1)
                  ])
                ], 10, _hoisted_11)
              ], 64))
            }), 128))
          ])
        : (_openBlock(), _createBlock(EmptyState, {
            key: 1,
            "is-insde-element": ""
          }, {
            default: _withCtx(() => [
              _createTextVNode(_toDisplayString(vm.validationFeedback), 1)
            ]),
            _: 1
          }))
    ], 46, _hoisted_9)
  ], 4))
}
}

})